import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { materialsApi, type Material } from '@/api/materials';
import { Modal } from '@/components/ui/Modal';
import { Watermark } from '@/components/protection/Watermark';
import { InlineYouTube } from '@/components/media/InlineYouTube';
import { YouTubePlayer } from '@/components/media/YouTubePlayer';
import { isStoreBuild } from '@/lib/platform';
import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { extractYouTubeId } from '@/lib/youtube';

/**
 * "Материалы" — curated dashboard feed of YouTube videos + rich-text articles.
 * Horizontal card carousel; opening a card marks it viewed (awarding points the
 * first time, server-side) then refetches so the viewed/points state updates.
 * Mirrors the stories carousel pattern on StudentDashboardPage.
 */

export function coverFor(m: Material): string | null {
  if (m.type === 'video') {
    const id = extractYouTubeId(m.youtube_url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : (m.cover_url ?? null);
  }
  return m.cover_url ?? null;
}

/**
 * `row` — лента вбок, как было и как осталось на телефоне.
 * `grid` — две колонки из макета «Desktop Web v2» для кабинета на компьютере.
 */
type Layout = 'row' | 'grid';

export function MaterialsWidget({ title, layout = 'row' }: { title?: string; layout?: Layout }) {
  const { t } = useTranslation();
  const grid = layout === 'grid';
  const heading = title ?? t('materials.title');
  const [active, setActive] = useState<Material | null>(null);

  const materials = useQuery({
    queryKey: ['student', 'materials'],
    queryFn: materialsApi.listForStudent,
  });

  const open = async (m: Material) => {
    setActive(m);
    // Award-on-open (idempotent server-side); refetch so viewed/points dim.
    try {
      await materialsApi.markViewed(m.id);
      await materials.refetch();
    } catch {
      /* viewing should never block the modal; ignore transient failures */
    }
  };

  if (materials.isLoading)
    return (
      <section className="mt-8">
        <h2 className="mb-3 font-display text-xl font-bold tracking-display">{heading}</h2>
        <div className="flex gap-4 overflow-x-auto pb-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 w-48 shrink-0 animate-pulse rounded-card bg-surface-container-low" />
          ))}
        </div>
      </section>
    );

  const items = materials.data ?? [];
  if (items.length === 0) return null;

  if (grid)
    return (
      <section>
        <div className="mx-0.5 mb-2.5 mt-5 flex items-baseline justify-between">
          <h2 className="font-display text-[15.5px] font-bold text-on-surface">{heading}</h2>
          <Link to="/app/materials" className="text-[12.5px] font-semibold text-info hover:underline">
            {t('dashboard.allMaterials')}
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {items.map((m) => {
            const cover = coverFor(m);
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => open(m)}
                aria-label={m.title}
                className={cn('group text-left', m.viewed && 'opacity-60')}
              >
                <div className="relative h-[104px] overflow-hidden rounded-[15px] bg-surface-container">
                  {cover ? (
                    <img
                      src={cover}
                      alt=""
                      className="h-full w-full object-cover"
                      onContextMenu={(e) => e.preventDefault()}
                      draggable={false}
                    />
                  ) : (
                    <span className="grid h-full w-full place-items-center text-on-surface-variant">
                      <span className="material-symbols-outlined text-2xl">
                        {m.type === 'video' ? 'play_circle' : 'article'}
                      </span>
                    </span>
                  )}
{/* ⚠️ ПЛАШКА «БАЛЛЫ: N» УБРАНА 10.09.2026 (разбаловка). Очки за
                    просмотр материала больше не начисляются нигде — очко на
                    платформе теперь значит «оценка за урок», и ничего кроме.
                    Плашка обещала ученику то, чего он не получит: открыл
                    материал и увидел, что очков не прибавилось.
                    Колонка materials.points в базе осталась — это единственная
                    запись прежнего замысла, — но показывать её нельзя. */}
                </div>
                <p className="mx-0.5 mb-0.5 mt-2 line-clamp-2 text-[12.5px] font-semibold leading-[1.3] text-on-surface">
                  {m.title}
                </p>
                <p className="mx-0.5 text-[11.5px] text-on-surface-variant">
                  {t(m.type === 'video' ? 'materials.typeVideo' : 'materials.typeArticle')}
                </p>
              </button>
            );
          })}
        </div>

        <Modal open={active !== null} onClose={() => setActive(null)} title={active?.title} size="xl">
          {active && <MaterialReader material={active} />}
        </Modal>
      </section>
    );

  return (
    <section className="mt-8">
      <h2 className="mb-3 font-display text-xl font-bold tracking-display">{heading}</h2>
      <div className="flex gap-4 overflow-x-auto pb-3">
        {items.map((m) => {
          const cover = coverFor(m);
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => open(m)}
              aria-label={m.title}
              className={cn(
                'group relative w-48 shrink-0 overflow-hidden rounded-card bg-surface-container-low text-left ghost-border transition hover:shadow-ambient',
                m.viewed && 'opacity-60',
              )}
            >
              <div className="relative aspect-video w-full overflow-hidden bg-surface-container">
                {cover ? (
                  <img
                    src={cover}
                    alt=""
                    className="h-full w-full object-cover"
                    onContextMenu={(e) => e.preventDefault()}
                    draggable={false}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-on-surface-variant">
                    <span className="material-symbols-outlined text-4xl">article</span>
                  </div>
                )}
                <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-inverse-surface/60 text-inverse-on-surface">
                  <span className="material-symbols-outlined text-[18px]">
                    {m.type === 'video' ? 'play_arrow' : 'article'}
                  </span>
                </span>
              </div>
              <div className="p-3">
                <p className="line-clamp-2 text-sm font-semibold leading-tight text-on-surface">{m.title}</p>
                {m.description && (
                  <p className="mt-1 line-clamp-2 text-xs text-on-surface-variant">{m.description}</p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <Modal
        open={active !== null}
        onClose={() => setActive(null)}
        title={active?.title}
        size="xl"
      >
        {active && <MaterialReader material={active} />}
      </Modal>
    </section>
  );
}

/**
 * Открытый материал: ролик или статья.
 *
 * ⚠️ В ПРИЛОЖЕНИИ РОЛИК УХОДИТ В СИСТЕМНОЕ ОКНО. Ровно та же причина, что и в
 * уроке: YouTube отвергает внутренние адреса приложения («Ошибка 153»), а рамку
 * с нашим доменом внутри не переживает браузерный движок. Найдено при сверке
 * экранов 24.08.2026 — здесь оставался старый способ.
 */
export function MaterialReader({ material }: { material: Material }) {
  const { t } = useTranslation();

  if (material.type === 'video') {
    const id = extractYouTubeId(material.youtube_url);
    if (!id) {
      return <p className="text-sm text-on-surface-variant">{t('materials.videoUnavailable')}</p>;
    }
    // То же, что в уроке: простая вставка прямо здесь, без отдельного окна.
    if (isStoreBuild()) {
      return (
        <div className="space-y-3">
          <InlineYouTube videoId={id} title={material.title} />
          {material.description && (
            <p className="text-sm text-on-surface-variant">{material.description}</p>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <Watermark>
          <YouTubePlayer videoId={id} title={material.title} />
        </Watermark>
        {material.description && (
          <p className="text-sm text-on-surface-variant">{material.description}</p>
        )}
      </div>
    );
  }

  // Article — reuse the TextBlock article renderer approach (sanitized HTML).
  return (
    <div className="space-y-3">
      {material.description && (
        <p className="text-sm text-on-surface-variant">{material.description}</p>
      )}
      <div
        className="max-w-none text-on-surface leading-body [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:font-display [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-on-surface [&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:font-semibold [&_h3]:text-on-surface [&_a]:text-primary [&_a]:underline [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p]:leading-body"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(material.body_html) }}
      />
    </div>
  );
}
